import { AddOnAvailability, GetAddOnResponse } from '../../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query } from 'lit/decorators';
import { isDefined, undefinedIfEmpty } from '../../helpers/utils';

import { StateController } from '../../helpers/state/state-management/state-controller';
import accountInfo from '../../helpers/state/account-info';
import featuresInfo from '../../helpers/state/features-info';
import { openChatWindow } from '../../helpers/intercom';
import subscriptionInfo from '../../helpers/state/subscription-info';
import topicData from '../../helpers/state/topic-data';

@customElement('admin-addon-renderer')
export class AdminAddonRenderer extends LitElement {
    private readonly amplitude_callsite: string = 'Addons Render';



    @query('#upgradeDialogComponent')
    upgradeDialogComponent!: HTMLDialogElement;

    @property()
    selectedAddon!: GetAddOnResponse;

    static amplitude_callsite: string = 'Admin Addon Renderer';

    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindTopicState = new StateController(this, topicData);

    private getAddonCta(addon: GetAddOnResponse) {
        switch (addon.availability) {
            case AddOnAvailability.GetNotified:
                return 'Get Notified';
            case AddOnAvailability.Available:
                if (featuresInfo.addOnEnabled(addon)) {
                    return 'Activate';
                } else if (subscriptionInfo.isTrial) {
                    return 'Add to Trial';
                } else if (subscriptionInfo.isActive) {
                    return 'Add to Subscription';
                } else {
                    return 'Upgrade to Activate';
                }
            case AddOnAvailability.ComingSoon:
                return 'Get Notified';
            default:
                return 'Activate';
        }
    }

    private unsetActiveAddon() {
        this.dispatchEvent(new CustomEvent('close-addon'));
    }

    toggleVideo() {
        // open video in new tab
        window.open(this.selectedAddon?.video_demo_link, '_blank');
    }

    toggleMarketingSite() {
        // open marketing site in new tab
        window.open(this.selectedAddon?.marketing_page_link, '_blank');
    }

    async actionClick() {
        if (featuresInfo.addOnEnabled(this.selectedAddon)) {
            await topicData.installAddon(this.amplitude_callsite, this.selectedAddon);
        } else {
            const entitlement = this.selectedAddon?.required_entitlements?.[0] ?? undefined;
            this.dispatchEvent(
                new CustomEvent('open-upgrade-dialog', {
                    detail: {
                        featureType: entitlement,
                    },
                })
            );
        }
    }

    protected render() {
        return html`
            ${topicData.activatedAddons.includes(this.selectedAddon) ?
                html`
                    <div class="addon__container">
                        <div style="position:fixed">
                            <mwc-icon-button @click=${this.unsetActiveAddon} class="setting-controls-button__icon" icon="arrow_back"></mwc-icon-button>
                        </div>
                        <div class="addon__title">
                            <div class="addon__preview-image-wrapper">
                                <div class="addon_icon-holder">
                                    <mwc-icon class="addon__icon">${this.selectedAddon.icon_path}</mwc-icon>
                                </div>
                                <img class="addon__preview-image-background" src="/images/addon-icons/addonbackground.png"/>
                            </div>
                            <h3>${this.selectedAddon.title}</h3>
                        </div>
                        <h4>${this.selectedAddon.subtitle}</h4>
                        <p style="flex: 1;">
                            ${this.selectedAddon.description}
                        </p>
                        <form-button class="addon__install" @click="${async () => await topicData.uninstallAddon(this.amplitude_callsite, this.selectedAddon)}">Hide from Main Menu</form-button>
                    </div>
                `: html`
                        <div class="addon__container">
                        <div style="position:fixed">
                            <mwc-icon-button @click=${this.unsetActiveAddon} class="setting-controls-button__icon" icon="arrow_back"></mwc-icon-button>
                        </div>
                        <div class="addon__title">
                            <div class="addon__preview-image-wrapper">
                                <div class="addon_icon-holder">
                                    <mwc-icon class="addon__icon">${this.selectedAddon.icon_path}</mwc-icon>
                                </div>
                                <img class="addon__preview-image-background" src="/images/addon-icons/addonbackground.png"/>
                            </div>
                            <h3>${this.selectedAddon.title}</h3>
                        </div>
                        <div class="help-container">
                            ${isDefined(this.selectedAddon?.marketing_page_link) ? html`
                                <form-button
                                    variant="outline"
                                    size="xs"
                                    class="buttonSection ${isDefined(this.selectedAddon?.marketing_page_link) ? 'hidden' : ''}"
                                    @click="${this.toggleMarketingSite}">
                                    <mwc-icon class="shareButtonIcon">web</mwc-icon>
                                    <span class="buttonText">About Feature</span>
                                </form-button>
                            ` : ''}
                            ${isDefined(this.selectedAddon?.video_demo_link) ? html`
                                <form-button
                                    variant="outline"
                                    size="xs"
                                    class="buttonSection ${isDefined(undefinedIfEmpty(this.selectedAddon?.video_demo_link)) ? 'hidden' : ''}"
                                    @click="${this.toggleVideo}">
                                    <mwc-icon class="shareButtonIcon">play_circle</mwc-icon>
                                    <span class="buttonText">Demo Video</span>
                                </form-button>
                            ` : ''}
                            ${isDefined(this.selectedAddon?.documentation_link) ? html`
                                <form-button
                                    variant="outline"
                                    size="xs"
                                    class="buttonSection ${isDefined(this.selectedAddon?.documentation_link) ? 'hidden' : ''}"
                                    @click="${this.toggleVideo}">
                                    <mwc-icon class="shareButtonIcon">article</mwc-icon>
                                    <span class="buttonText">Documentation</span>
                                </form-button>
                            ` : ''}
                        </div>
                        <h4>${this.selectedAddon.subtitle}</h4>
                        <p style="flex: 1;overflow: auto;">
                            ${this.selectedAddon.description}
                        </p>
                        <form-button class="addon__install" @click="${this.actionClick}">${this.getAddonCta(this.selectedAddon)}</form-button>
                        ${this.selectedAddon.availability === AddOnAvailability.Available && subscriptionInfo.isActive && !featuresInfo.addOnEnabled(this.selectedAddon) ?
                            html`
                                <span class="request_trial" @click="${() => openChatWindow(this.amplitude_callsite, 'I am interested in trialing the ' + this.selectedAddon.title + ' app')}">
                                    Request 7 day of trial of ${this.selectedAddon.title}
                                </span>
                            ` : ''}
                    </div>
                `
            }
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`

            /* width */
            .addon__container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .addon__container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .addon__container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .addon__container {
                overflow: hidden auto;
                margin-bottom: 1rem;
                padding-right: 20px;
                display: flex;
                flex-direction: column;
                max-height: calc(100vh - 110px);
            }
            .addons__wrapper {
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
            }

            .addon__title {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
            }
            h3 {
                margin-top: 5px
            }

            .addon__preview-image-wrapper {
                height: 50px;
                display: flex;
                justify-content: center;
            }

            .addon__preview-image-background {
                height: 100%
            }

            .addon_icon-holder {
                width: 0;
            }

            .shareButtonIcon {
                --mdc-icon-size: 0.8rem;
            }

            .addon__icon {
                position: relative;
                left: 17px;
                top: 17px;
                --mdc-icon-size: 16px;
            }

            .request_trial {
                width: 100%;
                text-align: center;
                text-decoration: underline;
                cursor: pointer;
                margin-top: 10px;
            }

            .buttonSection {
                background-color: var(--theme-white-color);
            }

            .help-container {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
                padding-right: 10px;
                gap: 5px;
                position: relative;
                top: -5px;
                height: 10px;
                z-index: 2;
                margin-top: 10px;
                margin-bottom: 10px;
            }

            .buttonText {
                margin-left: 5px;
            }

            .addon__install {
                margin-top: 10px;
                --mdc-typography-button-text-transform: case;
            }
        `;
    }
}
